import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useSocket } from '../contexts/SocketContext';

const POLL_MS = 2500;
const WS_STORAGE_KEY = 'wingr.scraperV2.sourcePerf.workspaceId';

function readStoredWorkspace() {
  try {
    return localStorage.getItem(WS_STORAGE_KEY) || 'legacy';
  } catch {
    return 'legacy';
  }
}

const COLUMNS = [
  { key: 'username', label: 'Username', align: 'left', rank: false },
  { key: 'firstScrapedAt', label: 'Started scraping', align: 'left', rank: true },
  { key: 'storedUnique', label: 'Followers stored', align: 'right', rank: true },
  // "Dispatched" = usernames from this source that live in the Redis
  // dispatch map of a currently-active follow job. Rows with dispatched > 0
  // get the orange gradient so the operator can see at a glance which
  // sources the automation is drawing from right now.
  { key: 'dispatched', label: 'Dispatched', align: 'right', rank: true },
  { key: 'followed', label: 'Followed', align: 'right', rank: true },
  { key: 'followedBack', label: 'Follow-backs', align: 'right', rank: true },
  { key: 'followbackRate', label: 'FB %', align: 'right', rank: true },
  { key: 'conversations', label: 'Conversations', align: 'right', rank: true },
  { key: 'followToConv', label: 'Follow vs conv', align: 'right', rank: true },
];

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function pct(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Shallow-compare two { key: number } maps. Used to skip `setActiveDispatch`
 * when the poll returned the same live dispatch counts we already have — a
 * fresh object identity every 2.5s otherwise invalidates the `filtered`
 * useMemo, re-renders every row, and causes the layout to twitch during
 * recompute. Keys with value 0 on one side and missing on the other are
 * treated as equal (0 renders as "—" identically).
 */
function shallowEqualNumMap(a, b) {
  if (a === b) return true;
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  const nonZeroA = ak.filter((k) => Number(a[k]) > 0);
  const nonZeroB = bk.filter((k) => Number(b[k]) > 0);
  if (nonZeroA.length !== nonZeroB.length) return false;
  for (const k of nonZeroA) {
    if (Number(a[k]) !== Number(b[k])) return false;
  }
  return true;
}

/**
 * Shallow-compare two arrays of primitives (order-insensitive) used to guard
 * `setCohorts` / `setQuality` when the overlay poll returned the same set of
 * usernames — avoids swapping the Set/Map identity every 30s and re-running
 * the sort + badge render for every row.
 */
function sameStringSet(a, b) {
  if (a === b) return true;
  const sa = a instanceof Set ? a : new Set(a || []);
  const sb = b instanceof Set ? b : new Set(b || []);
  if (sa.size !== sb.size) return false;
  for (const v of sa) if (!sb.has(v)) return false;
  return true;
}

function sortValue(row, key) {
  if (key === 'username') return null;
  if (key === 'firstScrapedAt') {
    const t = Date.parse(row.firstScrapedAt);
    return Number.isNaN(t) ? null : t;
  }
  const n = row[key] == null ? null : Number(row[key]);
  return n == null || Number.isNaN(n) ? null : n;
}

function compareRows(a, b, key, dir) {
  if (key === 'username') {
    const cmp = String(a.username || '').localeCompare(String(b.username || ''));
    return dir === 'asc' ? cmp : -cmp;
  }
  const an = sortValue(a, key);
  const bn = sortValue(b, key);
  if (an == null && bn == null) return String(a.username || '').localeCompare(String(b.username || ''));
  if (an == null) return 1;
  if (bn == null) return -1;
  const cmp = an - bn;
  if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
  return String(a.username || '').localeCompare(String(b.username || ''));
}

function RankToggle({ active, dir, onHigh, onLow }) {
  const btn = (label, selected, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-1.5 py-0.5 rounded ${
        selected
          ? 'bg-indigo-600 text-white'
          : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wide">
      {btn('High', active && dir === 'desc', onHigh)}
      {btn('Low', active && dir === 'asc', onLow)}
    </div>
  );
}

/**
 * Lifecycle badge for one source. Shows where the source sits in the
 * bounded-spend state machine plus the number that matters for that state:
 * budget burn while on trial, when the verdict lands while maturing, and the
 * measured rate once judged.
 */
function LifecycleBadge({ life }) {
  if (!life) return null;
  const state = life.dispatchState || 'untested';
  const rate = typeof life.lastVerdictRatePct === 'number'
    ? `${life.lastVerdictRatePct.toFixed(2)}%`
    : null;
  const bounds = (typeof life.lastVerdictLowerPct === 'number' && typeof life.lastVerdictUpperPct === 'number')
    ? ` (${life.lastVerdictLowerPct.toFixed(2)}–${life.lastVerdictUpperPct.toFixed(2)}%)`
    : '';

  const map = {
    proven: {
      label: rate ? `Proven · ${Number(life.lastVerdictRatePct).toFixed(1)}%` : 'Proven',
      cls: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      title: `Proven — uncapped dispatch${rate ? ` at ${rate}${bounds}` : ''}. ${life.dispatchedCount || 0} follows sent.`,
    },
    trial: {
      label: `Trial ${life.trialStage || 1} · ${life.dispatchedCount || 0}/${life.dispatchBudget || 0}`,
      cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
      title: `On trial — capped at ${life.dispatchBudget || 0} lifetime follows, ${life.dispatchedCount || 0} spent. Cannot exceed this budget.`,
    },
    awaiting_verdict: {
      label: life.verdictDueAt
        ? `Maturing · ${new Date(life.verdictDueAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
        : 'Maturing',
      cls: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
      title: `Budget spent (${life.dispatchedCount || 0} follows). No further dispatch until follow-backs land and the verdict is taken.`,
    },
    parked: {
      label: rate ? `Parked · ${Number(life.lastVerdictRatePct).toFixed(1)}%` : 'Parked',
      cls: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      title: `Parked — ${life.dispatchStateReason || 'below the follow-back floor'}. Claims released, scraping stopped.`,
    },
  };
  const badge = map[state];

  // Keep Proven/Trial + Dry on one line — flex-wrap on the parent username
  // cell used to drop Dry under a long "Proven · 19.09%" chip and inflate
  // the row. nowrap + shrink-0 keeps the badge group compact.
  return (
    <span className="inline-flex items-center gap-1 flex-nowrap shrink-0">
      {badge ? (
        <span className={`text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap ${badge.cls}`} title={badge.title}>
          {badge.label}
        </span>
      ) : null}
      {life.poolExhausted ? (
        <span
          className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
          title={
            life.rescrapePriority > 0
              ? 'No unclaimed rows left — queued for re-scrape so dispatch can resume.'
              : 'No unclaimed rows left for this source.'
          }
        >
          {life.rescrapePriority > 0 ? 'Dry · re-scrape queued' : 'Dry'}
        </span>
      ) : null}
    </span>
  );
}

async function fetchQualityRows(workspaceId, status) {
  const rows = [];
  let total = 0;
  let offset = 0;
  for (let page = 0; page < 40; page += 1) {
    const res = await api.get('/admin/scrape-v2/source-quality', {
      params: { workspaceId, status, limit: 500, offset },
    });
    const batch = res.data?.rows || [];
    total = Number(res.data?.total) || rows.length + batch.length;
    rows.push(...batch);
    offset += batch.length;
    if (!batch.length || rows.length >= total) break;
  }
  return { rows, total };
}

export default function ScraperV2SourcePerformanceTab() {
  const { socket } = useSocket();
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(readStoredWorkspace);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sortKey, setSortKey] = useState('followbackRate');
  const [sortDir, setSortDir] = useState('desc');
  // Auto-optimize overlay: per-source badges (top/explore/cool-down) come
  // from the union of every live agent's cohorts on this workspace, and
  // quality-gate flags (paused/excluded) come from the scrape registry.
  const [quality, setQuality] = useState({ paused: new Map(), excluded: new Map() });
  const [cohorts, setCohorts] = useState({ top: new Set(), explore: new Set(), cool: new Set(), summary: null });
  // Dispatch lifecycle overlay. When auto-optimize runs in bounded-spend mode
  // this supersedes the top/explore/cool-down cohorts above: each source is a
  // trial with a lifetime budget, then proven or parked. Empty map = the
  // workspace is still on the legacy cohort model.
  const [lifecycle, setLifecycle] = useState({ byUsername: new Map(), counts: null });
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  // Live per-source dispatched counts from currently-active follow jobs.
  // { sourceUsername (lowercased): number }. Empty when no job is running.
  const [activeDispatch, setActiveDispatch] = useState({});
  const [showOnlyPicks, setShowOnlyPicks] = useState(false);
  const [hidePaused, setHidePaused] = useState(false);
  const [hideExcluded, setHideExcluded] = useState(true);
  const [rowActionBusy, setRowActionBusy] = useState('');

  const running = snapshot?.status === 'running';
  const selectedWorkspace = workspaces.find((w) => w.workspaceId === workspaceId);
  const workspaceLabel = selectedWorkspace?.name
    || (workspaceId === 'legacy' ? 'Current data (legacy)' : workspaceId);

  // ── Smooth overall-progress model ────────────────────────────────────────
  // The backend emits per-phase counters that RESET across phases (stored,
  // ledger, scraped each start at 0), so a naive bar visibly snaps backward
  // 3 times during a ~20s run. Instead we weight each phase into a fixed
  // slice of the overall bar and clamp to the max reached value within one
  // run (so the bar can never regress).
  const PHASE_WEIGHTS = useMemo(() => ({
    starting:      { from: 0,   to: 2 },
    sources:       { from: 2,   to: 5 },
    stored:        { from: 5,   to: 55 },
    ledger:        { from: 55,  to: 65 },
    conversations: { from: 65,  to: 70 },
    scraped:       { from: 70,  to: 100 },
    done:          { from: 100, to: 100 },
    error:         { from: 0,   to: 0 },
  }), []);

  // Anchor holds the highest pct we've displayed *for the current runId*.
  // Used as a floor so the bar can't regress across phase transitions.
  // Guarded by runId so a stale anchor from a completed run never leaks
  // into the next run's rendering (that was the "stuck at 100%" bug).
  const [progressAnchor, setProgressAnchor] = useState({ runId: null, pct: 0 });

  const overallPct = useMemo(() => {
    if (!snapshot) return 0;
    if (snapshot.status === 'done') return 100;
    if (snapshot.status !== 'running') return 0;
    const slice = PHASE_WEIGHTS[snapshot.phase] || { from: 0, to: 100 };
    const seen = Number(snapshot.scrapedSeen || 0);
    const total = Number(snapshot.progressTotal || 0);
    const inner = total > 0 ? Math.min(1, seen / total) : 0.3;
    return slice.from + (slice.to - slice.from) * inner;
  }, [snapshot, PHASE_WEIGHTS]);

  // Compute displayPct SYNCHRONOUSLY from overallPct + a matching anchor.
  // The anchor only contributes when its runId equals the current run's —
  // otherwise it's ignored (fixes stale-run bleed-through where a done=100
  // anchor from run A was showing during run B's stored phase).
  const displayPct = useMemo(() => {
    if (!snapshot) return 0;
    if (snapshot.status === 'done') return 100;
    if (snapshot.status !== 'running') return 0;
    const floor = (progressAnchor.runId && progressAnchor.runId === snapshot.runId)
      ? progressAnchor.pct
      : 0;
    return Math.max(4, Math.max(overallPct, floor));
  }, [snapshot, overallPct, progressAnchor]);

  // Persist the highest pct we've seen for this runId, so the next render
  // still respects the monotonic floor. Runs *after* the render that
  // already computed displayPct correctly — so a 1-frame stale value can
  // never leak to the user.
  useEffect(() => {
    if (!snapshot?.runId) return;
    setProgressAnchor((prev) => {
      if (prev.runId !== snapshot.runId) {
        return { runId: snapshot.runId, pct: displayPct };
      }
      return displayPct > prev.pct ? { runId: snapshot.runId, pct: displayPct } : prev;
    });
  }, [snapshot?.runId, displayPct]);

  // Keep the last non-empty phase label visible during the brief window
  // where a new phase has kicked off but its counters are still zeroed —
  // stops the label from flashing to "Starting…" mid-run.
  const [stickyLabel, setStickyLabel] = useState('');
  useEffect(() => {
    if (!snapshot?.runId || snapshot.status !== 'running') { setStickyLabel(''); return; }
    const raw = {
      starting: 'Starting…',
      sources: 'Listing source usernames…',
      stored: 'Counting stored followers…',
      conversations: 'Loading conversations…',
      ledger: 'Scanning follows…',
      scraped: 'Matching follows to scrape sources…',
    }[snapshot.phase];
    if (raw) setStickyLabel(raw);
  }, [snapshot?.runId, snapshot?.status, snapshot?.phase]);

  const applySnapshot = useCallback((next, forWorkspace, { fromSocket } = {}) => {
    if (!next) return;
    const incomingWs = next.workspaceId || forWorkspace || 'legacy';
    if (incomingWs !== workspaceId) return;
    // Ignore leftover pre-speedup socket ticks (10-at-a-time counter).
    if (fromSocket && next.status === 'running' && Number(next.snapshotVersion || 0) < 2) return;
    setSnapshot((prev) => {
      const incoming = (next.rowsOmitted && prev)
        ? { ...next, rows: prev.rows || [], rowsOmitted: false }
        : next;
      if (!prev) return incoming;
      // A newer compute always wins.
      if (prev.runId != null && incoming.runId != null && prev.runId !== incoming.runId) {
        const prevStart = prev.startedAt ? new Date(prev.startedAt).getTime() : 0;
        const nextStart = incoming.startedAt ? new Date(incoming.startedAt).getTime() : 0;
        if (nextStart && prevStart && nextStart < prevStart) return prev;
        return incoming;
      }
      // Same run: never let a stale poll/socket tick move the bar backwards in-phase.
      if (
        prev.status === 'running'
        && incoming.status === 'running'
        && prev.phase
        && incoming.phase === prev.phase
        && Number(incoming.scrapedSeen || 0) < Number(prev.scrapedSeen || 0)
      ) {
        return prev;
      }
      return incoming;
    });
    if (next.status !== 'running') setRefreshing(false);
  }, [workspaceId]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/scraper-v2/source-performance', {
        params: { workspaceId },
      });
      if (res.data.success) {
        applySnapshot(res.data.snapshot, workspaceId);
        // Live dispatch map — only replace when it actually differs; otherwise
        // every 2.5s poll creates a new object identity, invalidates the
        // `filtered` memo, and re-renders the whole table — which is exactly
        // what causes the scroll to twitch during recompute.
        const next = res.data.activeDispatchSources || {};
        setActiveDispatch((prev) => (shallowEqualNumMap(prev, next) ? prev : next));
      }
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Failed to load source performance');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applySnapshot, workspaceId]);

  useEffect(() => {
    api.get('/scraper-v2/workspaces')
      .then((res) => {
        if (res.data?.success) setWorkspaces(res.data.workspaces || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(WS_STORAGE_KEY, workspaceId); } catch { /* ignore */ }
    setSnapshot(null);
    load();
  }, [workspaceId, load]);

  useEffect(() => {
    if (!socket) return undefined;
    const onUpdate = (payload) => applySnapshot(payload, workspaceId, { fromSocket: true });
    socket.on('scraper-v2-source-performance', onUpdate);
    return () => socket.off('scraper-v2-source-performance', onUpdate);
  }, [socket, applySnapshot, workspaceId]);

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  const startRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/scraper-v2/source-performance/refresh', { workspaceId });
      if (res.data.success) {
        applySnapshot(res.data.snapshot, workspaceId);
        if (res.data.snapshot?.status === 'running') {
          toast.success('Computing this workspace…');
        }
      }
    } catch (err) {
      setRefreshing(false);
      toast.error(err.response?.data?.error || 'Could not start compute');
    }
  };

  const loadQualityOverlay = useCallback(async () => {
    try {
      const [pausedRes, excludedRes, summaryRes, lifecycleRes] = await Promise.all([
        fetchQualityRows(workspaceId, 'paused').catch(() => null),
        fetchQualityRows(workspaceId, 'excluded').catch(() => null),
        api.get(`/admin/scrape-v2/workspace/${encodeURIComponent(workspaceId)}/optimizer-summary`)
          .catch(() => null),
        api.get('/admin/scrape-v2/lifecycle-map', {
          params: { workspaceId, limit: 3000 },
        }).catch(() => null),
      ]);

      const lifeMap = new Map();
      for (const row of lifecycleRes?.data?.sources || []) {
        lifeMap.set(String(row.username || '').toLowerCase(), row);
      }
      setLifecycle((prev) => (
        sameStringSet(prev.byUsername.keys(), lifeMap.keys())
        && prev.counts
        && JSON.stringify(prev.counts) === JSON.stringify(lifecycleRes?.data?.counts || null)
          ? prev
          : { byUsername: lifeMap, counts: lifecycleRes?.data?.counts || null }
      ));
      const paused = new Map();
      for (const row of pausedRes?.rows || []) {
        paused.set(String(row.username || '').toLowerCase(), row);
      }
      const excluded = new Map();
      for (const row of excludedRes?.rows || []) {
        excluded.set(String(row.username || '').toLowerCase(), row);
      }
      // Skip setState when the set of paused/excluded usernames is unchanged
      // (per-row payload details rarely matter for what we render). Prevents
      // the 30s overlay poll from thrashing every row's badge render.
      setQuality((prev) => (
        sameStringSet(prev.paused.keys(), paused.keys())
        && sameStringSet(prev.excluded.keys(), excluded.keys())
          ? prev
          : { paused, excluded }
      ));

      const top = new Set();
      const explore = new Set();
      const cool = new Set();
      const agents = summaryRes?.data?.agents || [];
      // Fetch each active agent's optimizer state — union the cohorts across
      // agents so a source shows Top/Explore/Cool-down when ANY live agent
      // has it in that cohort.
      const stateResults = await Promise.all(agents.map((a) =>
        api.get(`/admin/va/ai-agents/${a.agentId}/optimizer/state`)
          .then((r) => r.data)
          .catch(() => null)
      ));
      for (const state of stateResults) {
        const c = state?.cohorts || {};
        for (const u of c.topSources || []) top.add(String(u).toLowerCase());
        for (const u of c.explorationSources || []) explore.add(String(u).toLowerCase());
        for (const u of c.coolDownSources || []) cool.add(String(u).toLowerCase());
      }
      setCohorts((prev) => (
        sameStringSet(prev.top, top)
        && sameStringSet(prev.explore, explore)
        && sameStringSet(prev.cool, cool)
          ? prev
          : { top, explore, cool, summary: summaryRes?.data || null }
      ));
    } catch (err) {
      // Overlay is optional — silent on failure.
    }
  }, [workspaceId]);

  useEffect(() => {
    loadQualityOverlay();
    const t = setInterval(loadQualityOverlay, 30_000);
    return () => clearInterval(t);
  }, [loadQualityOverlay]);

  const runRowAction = async (username, action) => {
    if (rowActionBusy) return;
    setRowActionBusy(`${action}:${username}`);
    try {
      if (action === 'rescore') {
        // Rescore is per-agent; hit the first live agent on this workspace.
        const agentId = cohorts.summary?.agents?.[0]?.agentId;
        if (!agentId) {
          toast.error('No live auto-optimize agent on this workspace to run rescore');
        } else {
          await api.post(`/admin/va/ai-agents/${agentId}/optimizer/rescore`, {
            actor: 'operator:ui',
          });
          toast.success('Rescore triggered');
        }
      } else {
        await api.post(
          `/admin/scrape-v2/source-quality/${encodeURIComponent(username)}/${action}`,
          { workspaceId, actor: 'operator:ui', reason: 'via Source Performance row action' }
        );
        toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)}d @${username}`);
      }
      await loadQualityOverlay();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action}`);
    } finally {
      setRowActionBusy('');
    }
  };

  /**
   * Move one source to a lifecycle state by hand. Parking through here does
   * the same recall an automatic park does — the source's unfollowed claims
   * are released so its tail stops.
   */
  const runLifecycleAction = async (username, state) => {
    if (rowActionBusy) return;
    const agentId = cohorts.summary?.agents?.[0]?.agentId;
    if (!agentId) {
      toast.error('No live auto-optimize agent on this workspace');
      return;
    }
    setRowActionBusy(`${state}:${username}`);
    try {
      await api.post(
        `/admin/va/ai-agents/${agentId}/optimizer/lifecycle/source/${encodeURIComponent(username)}`,
        { actor: 'operator:ui', state, reason: 'via Source Performance row action' }
      );
      toast.success(`@${username} → ${state.replace(/_/g, ' ')}`);
      await loadQualityOverlay();
    } catch (err) {
      toast.error(err.response?.data?.error || `Could not set ${state}`);
    } finally {
      setRowActionBusy('');
    }
  };

  const setRank = (key, dir) => {
    setSortKey(key);
    setSortDir(dir);
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setRank(key, key === 'username' ? 'asc' : 'desc');
    }
  };

  // Stabilize `rows` reference: during a running recompute the poll returns
  // a brand new snapshot object every 2.5s (progress ticks) but `rows` is
  // unchanged until `done`. Keying the memo on snapshotVersion+status
  // avoids re-sorting/re-enriching every row 24 times during a 60s run.
  const rows = useMemo(
    () => snapshot?.rows || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snapshot?.snapshotVersion, snapshot?.status, snapshot?.runId, snapshot?.rows?.length],
  );
  const filtered = useMemo(() => {
    const q = String(debouncedSearch || '').trim().toLowerCase().replace(/^@/, '');
    // Enrich rows with the live per-source dispatched count. Copies rows so
    // sorting on "dispatched" works and the memo re-runs when activeDispatch
    // ticks.
    let list = rows.map((r) => {
      const u = String(r.username || '').toLowerCase();
      const d = activeDispatch[u];
      return d ? { ...r, dispatched: d } : { ...r, dispatched: 0 };
    });
    if (q) list = list.filter((r) => String(r.username || '').includes(q));
    if (showOnlyPicks && (cohorts.top.size || cohorts.explore.size)) {
      list = list.filter((r) => {
        const u = String(r.username || '').toLowerCase();
        return cohorts.top.has(u) || cohorts.explore.has(u);
      });
    }
    if (hidePaused && quality.paused.size) {
      list = list.filter((r) => !quality.paused.has(String(r.username || '').toLowerCase()));
    }
    if (hideExcluded && quality.excluded.size) {
      list = list.filter((r) => !quality.excluded.has(String(r.username || '').toLowerCase()));
    }
    if (lifecycleFilter) {
      list = list.filter((r) => {
        const life = lifecycle.byUsername.get(String(r.username || '').toLowerCase());
        if (lifecycleFilter === 'exhausted') return Boolean(life?.poolExhausted);
        // Sources absent from the map have never been dispatched to.
        return (life?.dispatchState || 'untested') === lifecycleFilter;
      });
    }
    list.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return list;
  }, [rows, activeDispatch, debouncedSearch, sortKey, sortDir, showOnlyPicks, hidePaused, hideExcluded, cohorts, quality, lifecycleFilter, lifecycle]);

  const totals = snapshot?.totals || {};
  const computedAt = snapshot?.computedAt
    ? new Date(snapshot.computedAt).toLocaleString()
    : null;
  // Use the sticky label while a run is in flight so phase transitions
  // don't cause the label to blank/reflash. Final states use the raw label.
  const phaseLabel = snapshot?.status === 'done'
    ? 'Done'
    : snapshot?.status === 'error'
      ? 'Failed'
      : (running ? (stickyLabel || 'Computing…') : null);

  return (
    // `[overflow-anchor:auto]` explicitly re-enables the browser's default
    // scroll-anchoring for this subtree — some parent layouts inherit
    // `overflow-anchor:none` from utility resets, which would let content
    // above the viewport shift the scroll offset when snapshot polls tick.
    <div className="space-y-4 [overflow-anchor:auto]">
      {/*
        Progress banner — ALWAYS rendered so its ~76px worth of vertical
        space is reserved even when the recompute finishes. Otherwise the
        banner appearing on Start / disappearing on Done would shift every
        row below by 76px and yank the operator's scroll position.
        Contents are hidden (opacity + aria-hidden) when idle so nothing
        is announced to screen readers.
      */}
      <div
        aria-hidden={!running}
        className={`rounded-lg border px-4 py-3 min-h-[4.75rem] transition-opacity duration-200 ${
          running
            ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/30 opacity-100'
            : 'border-transparent bg-transparent opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center justify-between text-sm text-indigo-800 dark:text-indigo-200 mb-2">
          <span className="tabular-nums">
            {phaseLabel || '\u00a0'}
            {snapshot?.scrapedSeen
              ? ` · ${fmt(snapshot.scrapedSeen)}${snapshot.progressTotal ? ` / ${fmt(snapshot.progressTotal)}` : ''}`
              : ''}
          </span>
          <span className="tabular-nums">
            {running
              ? `${Math.round(displayPct)}% overall`
              : '\u00a0'}
          </span>
        </div>
        {/*
          Overall progress bar — phase-weighted and monotonic within a
          single run (see PHASE_WEIGHTS + progressAnchor upstream). The bar
          only moves forward, never snaps back across phase transitions.
          `will-change: width` hints the compositor for a smoother ramp.
        */}
        <div className="h-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900 overflow-hidden">
          <div
            className="h-full bg-indigo-500 transition-[width] duration-500 ease-out will-change-[width]"
            style={{ width: `${displayPct}%` }}
          />
        </div>
      </div>

      {snapshot?.status === 'error' && snapshot?.error && (
        <p className="text-sm text-red-600">{snapshot.error}</p>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 max-w-full flex-1">
            <span className="font-medium">Source performance</span>
            {/*
              Reserved height: this description grows from ~1 line at
              first paint to up to 3 lines once the quality / cohorts
              overlays land ~30s later. Without min-h it re-flows every
              time an async load lands and shifts the table below.
            */}
            <p className="text-xs text-gray-500 mt-1 min-h-[2.5rem] leading-5">
              {workspaceLabel}
              {' · '}{fmt(filtered.length)} of {fmt(rows.length)} sources
              {search.trim() ? ` · “${search.trim()}”` : ''}
              {computedAt ? ` · computed ${computedAt}` : ' · not computed yet'}
              {totals.attributedConversations != null
                ? ` · ${fmt(totals.attributedConversations)} chats from this workspace’s follows`
                : ''}
              {(quality.paused.size || quality.excluded.size)
                ? ` · ${quality.paused.size} paused · ${quality.excluded.size} excluded`
                : ''}
              {(cohorts.top.size || cohorts.explore.size)
                ? ` · auto-optimize: ${cohorts.top.size} top · ${cohorts.explore.size} explore · ${cohorts.cool.size} cool-down`
                : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 min-w-[14rem]"
            >
              {(workspaces.length
                ? workspaces
                : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]
              ).map((w) => (
                <option key={w.workspaceId} value={w.workspaceId}>
                  {w.name}
                  {w.followBacks != null ? ` — ${fmt(w.followBacks)} follow-backs` : ''}
                </option>
              ))}
            </select>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search @username…"
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 min-w-[12rem]"
            />
            <button
              type="button"
              disabled={running || refreshing}
              onClick={startRefresh}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
              {rows.length ? 'Recompute' : 'Compute'}
            </button>
          </div>
        </div>
        {/*
          Filter chip bar — always rendered so its ~34px doesn't jump into
          existence when the async overlay loads. When there are no chips
          it renders an empty (but height-stable) row.
        */}
        {(() => {
          const lc = lifecycle.counts;
          const hasLifecycle = Boolean(lc && (lc.trial || lc.awaiting_verdict || lc.proven || lc.parked));
          const hasChips = cohorts.top.size || cohorts.explore.size
            || quality.paused.size || quality.excluded.size || hasLifecycle;
          const lifecycleChips = [
            { key: 'proven', label: 'Proven', n: lc?.proven || 0, tone: 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' },
            { key: 'trial', label: 'Trial', n: lc?.trial || 0, tone: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' },
            { key: 'awaiting_verdict', label: 'Maturing', n: lc?.awaiting_verdict || 0, tone: 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300' },
            { key: 'parked', label: 'Parked', n: lc?.parked || 0, tone: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300' },
          ].filter((c) => c.n > 0);
          return (
          <div
            aria-hidden={!hasChips}
            className={`px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2 text-xs min-h-[2.375rem] ${
              hasChips ? '' : 'opacity-0 pointer-events-none'
            }`}
          >
            {(cohorts.top.size || cohorts.explore.size) ? (
              <button
                type="button"
                onClick={() => setShowOnlyPicks((v) => !v)}
                className={`px-2 py-1 rounded-full border ${
                  showOnlyPicks
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                Show only Auto-optimize picks
              </button>
            ) : null}
            {quality.paused.size ? (
              <button
                type="button"
                onClick={() => setHidePaused((v) => !v)}
                className={`px-2 py-1 rounded-full border ${
                  hidePaused
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                {hidePaused ? 'Hiding' : 'Hide'} quality-paused ({quality.paused.size})
              </button>
            ) : null}
            {quality.excluded.size ? (
              <button
                type="button"
                onClick={() => setHideExcluded((v) => !v)}
                className={`px-2 py-1 rounded-full border ${
                  hideExcluded
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                {hideExcluded ? 'Hiding' : 'Hide'} quality-excluded ({quality.excluded.size})
              </button>
            ) : null}
            {hasLifecycle ? (
              <>
                <span className="text-gray-300 dark:text-gray-700">|</span>
                {lifecycleChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setLifecycleFilter((v) => (v === chip.key ? '' : chip.key))}
                    className={`px-2 py-1 rounded-full border ${
                      lifecycleFilter === chip.key
                        ? chip.tone
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {chip.label} ({chip.n})
                  </button>
                ))}
              </>
            ) : null}
          </div>
          );
        })()}

        {loading && !rows.length ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : !rows.length && !running ? (
          <p className="p-4 text-sm text-gray-500">
            No snapshot yet for this database. Compute to rank every scraped
            source username by follows, follow-backs, and conversations.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-500 sticky top-0">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      <div className={`flex flex-col gap-1 ${col.align === 'right' ? 'items-end' : 'items-start'}`}>
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className={`inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200 ${
                            sortKey === col.key ? 'text-gray-800 dark:text-gray-100' : ''
                          }`}
                        >
                          {col.label}
                          {sortKey === col.key && !col.rank ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                        </button>
                        {col.rank && (
                          <RankToggle
                            active={sortKey === col.key}
                            dir={sortDir}
                            onHigh={() => setRank(col.key, 'desc')}
                            onLow={() => setRank(col.key, 'asc')}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const u = String(row.username || '').toLowerCase();
                  const life = lifecycle.byUsername.get(u);
                  // The lifecycle supersedes the cohort model. When a source
                  // has lifecycle state, its stale Top/Explore/Cool-down
                  // badges would contradict it — show one story, not two.
                  const isTop = !life && cohorts.top.has(u);
                  const isExplore = !life && cohorts.explore.has(u);
                  const isCool = !life && cohorts.cool.has(u);
                  const paused = quality.paused.get(u);
                  const excluded = quality.excluded.get(u);
                  const gated = paused || excluded;
                  // Live dispatch signal: this source is in a running job's
                  // Redis map right now. Paint a subtle orange gradient so
                  // sir Thomas can see at a glance where the automation is
                  // pulling from. Left→right fade keeps the row legible.
                  const dispatched = row.dispatched || 0;
                  const dispatchBg = dispatched > 0
                    ? 'bg-gradient-to-r from-orange-100/70 via-orange-50/40 to-transparent dark:from-orange-900/30 dark:via-orange-950/15 dark:to-transparent'
                    : '';
                  const rowClass = `border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/70 dark:hover:bg-gray-800/40 ${dispatchBg} ${
                    excluded ? 'opacity-60' : ''
                  }`;
                  return (
                    // Compact fixed height keeps badge overlays from shifting
                    // rows once cohorts/lifecycle load, without the old tall
                    // double-line Proven+Dry wrap.
                    <tr key={row.username} className={`${rowClass} h-[2.75rem]`}>
                      <td className="px-3 py-1 font-medium align-middle whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span>@{row.username}</span>
                          <LifecycleBadge life={life} />
                          {isTop && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                              title="Top-N ranked source across live auto-optimize agents"
                            >
                              Top
                            </span>
                          )}
                          {isExplore && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                              title="In the explore cohort — reserved for testing new/unproven sources"
                            >
                              Explore
                            </span>
                          )}
                          {isCool && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                              title="Passed sample gate but rate below cool-down floor"
                            >
                              Cool-down
                            </span>
                          )}
                          {paused && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
                              title={`Quality-paused${paused.qualityPausedAt ? ` at ${new Date(paused.qualityPausedAt).toLocaleString()}` : ''}${paused.qualityPauseSample ? ` · ${paused.qualityPauseSample.follows} follows @ ${paused.qualityPauseSample.ratePct?.toFixed?.(2)}% FB` : ''}${paused.lastRescoredRatePct != null ? ` · last rescored ${paused.lastRescoredRatePct.toFixed(2)}%` : ''}`}
                            >
                              Quality-paused
                            </span>
                          )}
                          {excluded && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded whitespace-nowrap bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                              title={`Quality-excluded${excluded.qualityExcludedAt ? ` at ${new Date(excluded.qualityExcludedAt).toLocaleString()}` : ''} — ${excluded.qualityExcludedReason || 'terminal'}`}
                            >
                              Quality-excluded
                            </span>
                          )}
                          <span className="ml-1 flex items-center gap-1 text-[10px] text-gray-500">
                            {/*
                              Lifecycle actions replace the quality pause/resume
                              pair once a source is in the state machine —
                              resuming a parked source without also moving it out
                              of `parked` would un-pause the scraper while
                              dispatch still refused to touch it.
                            */}
                            {life && !excluded ? (
                              <>
                                {life.dispatchState !== 'proven' && (
                                  <>
                                    <button
                                      type="button"
                                      className="hover:text-indigo-600 disabled:opacity-40"
                                      disabled={rowActionBusy === `proven:${row.username}`}
                                      onClick={() => runLifecycleAction(row.username, 'proven')}
                                    >Promote</button>
                                    <span>·</span>
                                  </>
                                )}
                                {life.dispatchState !== 'parked' && (
                                  <>
                                    <button
                                      type="button"
                                      className="hover:text-yellow-600 disabled:opacity-40"
                                      disabled={rowActionBusy === `parked:${row.username}`}
                                      onClick={() => runLifecycleAction(row.username, 'parked')}
                                    >Park</button>
                                    <span>·</span>
                                  </>
                                )}
                                <button
                                  type="button"
                                  className="hover:text-emerald-600 disabled:opacity-40"
                                  disabled={rowActionBusy === `trial:${row.username}`}
                                  onClick={() => runLifecycleAction(row.username, 'trial')}
                                >Re-test</button>
                                <span>·</span>
                                <button
                                  type="button"
                                  className="hover:text-red-600 disabled:opacity-40"
                                  disabled={rowActionBusy === `exclude:${row.username}`}
                                  onClick={() => runRowAction(row.username, 'exclude')}
                                >Exclude</button>
                              </>
                            ) : (
                              <>
                                {paused && (
                                  <>
                                    <button
                                      type="button"
                                      className="hover:text-indigo-600 disabled:opacity-40"
                                      disabled={rowActionBusy === `resume:${row.username}`}
                                      onClick={() => runRowAction(row.username, 'resume')}
                                    >Resume</button>
                                    <span>·</span>
                                    <button
                                      type="button"
                                      className="hover:text-indigo-600 disabled:opacity-40"
                                      disabled={rowActionBusy === `rescore:${row.username}`}
                                      onClick={() => runRowAction(row.username, 'rescore')}
                                    >Recheck</button>
                                    <span>·</span>
                                    <button
                                      type="button"
                                      className="hover:text-red-600 disabled:opacity-40"
                                      disabled={rowActionBusy === `exclude:${row.username}`}
                                      onClick={() => runRowAction(row.username, 'exclude')}
                                    >Exclude</button>
                                  </>
                                )}
                                {excluded && (
                                  <button
                                    type="button"
                                    className="hover:text-indigo-600 disabled:opacity-40"
                                    disabled={rowActionBusy === `resume:${row.username}`}
                                    onClick={() => runRowAction(row.username, 'resume')}
                                  >Resume</button>
                                )}
                                {!gated && (
                                  <button
                                    type="button"
                                    className="hover:text-orange-600 disabled:opacity-40"
                                    disabled={rowActionBusy === `pause:${row.username}`}
                                    onClick={() => runRowAction(row.username, 'pause')}
                                  >Pause</button>
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap align-middle">
                        {fmtDate(row.firstScrapedAt)}
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{fmt(row.storedUnique)}</td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">
                        {dispatched > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-medium"
                            title="Usernames from this source currently in the active follow-job dispatch map"
                          >
                            {fmt(dispatched)}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{fmt(row.followed)}</td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{fmt(row.followedBack)}</td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{pct(row.followbackRate)}</td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{fmt(row.conversations)}</td>
                      <td className="px-3 py-1 text-right tabular-nums align-middle">{pct(row.followToConv)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
